/**
 * Règles des notifications, hors React et hors store.
 *
 * Deux surfaces, deux rôles :
 *   • la PILE (bas-gauche) signale ce qui vient de se passer. Jamais plus de
 *     `MAX_VISIBLE_TOASTS` cartes, jamais persistante : c'est la version
 *     d'avant, empilée sans limite, qui montait jusqu'à sortir de l'écran ;
 *   • l'HISTORIQUE (cloche de la barre d'application) garde tout, survit au
 *     rechargement et contient le texte complet.
 *
 * Ce qui demande une action ne vit pas dans la pile : les chevauchements et
 * les trous ont leur barre sur la grille, calculée depuis la grille elle-même.
 */

export type NoticeSeverity = 'success' | 'error' | 'warning' | 'info'

/** Élément de grille vers lequel « Voir » amène — sérialisable, il survit au rechargement. */
export interface NoticeFocus {
  itemId: string
  iso:    string
  gridId: string
}

export interface NoticeEntry {
  id:       string
  message:  string
  severity: NoticeSeverity
  /** ISO du dernier déclenchement. */
  at:       string
  /** Nombre de déclenchements identiques regroupés. */
  count:    number
  read:     boolean
  focus?:   NoticeFocus
}

export const MAX_VISIBLE_TOASTS = 3
export const HISTORY_LIMIT = 150
/** Au-delà, un message identique ouvre une nouvelle ligne d'historique. */
export const HISTORY_MERGE_WINDOW_MS = 10 * 60 * 1000
export const NOTICE_STORAGE_KEY = 'trafficplay.notifications.v1'

/** Durée d'affichage dans la pile par gravité, en ms. */
export const DEFAULT_TOAST_DURATION: Record<NoticeSeverity, number> = {
  success: 4000,
  info:    5000,
  warning: 8000,
  error:   12000,
}

/**
 * Durée effective d'une carte. `0` (l'ancien « persistant ») retombe sur la
 * durée de sa gravité : la carte quitte la pile, le texte reste dans
 * l'historique, non lu, avec la pastille de la cloche.
 */
export function toastDuration(severity: NoticeSeverity, requested?: number): number {
  if (requested === undefined || requested <= 0) return DEFAULT_TOAST_DURATION[severity]
  return requested
}

/** Deux notifications sont « la même » si le texte et la gravité coïncident. */
export function noticeKey(message: string, severity: NoticeSeverity): string {
  return `${severity}:${message}`
}

/**
 * Ajoute un déclenchement à l'historique (le plus récent en tête). Un message
 * identique et récent est regroupé : compteur incrémenté, remonté en tête,
 * redevenu non lu.
 */
export function recordNotice(
  history: NoticeEntry[],
  notice: Omit<NoticeEntry, 'count' | 'read'>,
): NoticeEntry[] {
  const key = noticeKey(notice.message, notice.severity)
  const atMs = new Date(notice.at).getTime()
  const index = history.findIndex(entry =>
    noticeKey(entry.message, entry.severity) === key
    && atMs - new Date(entry.at).getTime() < HISTORY_MERGE_WINDOW_MS)

  if (index >= 0) {
    const merged: NoticeEntry = {
      ...history[index],
      at:    notice.at,
      count: history[index].count + 1,
      read:  false,
      focus: notice.focus ?? history[index].focus,
    }
    return [merged, ...history.slice(0, index), ...history.slice(index + 1)]
  }
  return [{ ...notice, count: 1, read: false }, ...history].slice(0, HISTORY_LIMIT)
}

const SEVERITIES = new Set<NoticeSeverity>(['success', 'error', 'warning', 'info'])

/** Relit l'historique persisté ; toute ligne mal formée est écartée. */
export function parseNoticeHistory(raw: string | null): NoticeEntry[] {
  if (!raw) return []
  try {
    const parsed: unknown = JSON.parse(raw)
    if (!Array.isArray(parsed)) return []
    return parsed.filter((entry): entry is NoticeEntry =>
      Boolean(entry)
      && typeof entry.id === 'string'
      && typeof entry.message === 'string'
      && SEVERITIES.has(entry.severity)
      && typeof entry.at === 'string'
      && Number.isFinite(new Date(entry.at).getTime())
      && typeof entry.count === 'number'
      && typeof entry.read === 'boolean',
    ).slice(0, HISTORY_LIMIT)
  } catch {
    return []
  }
}

export function loadNoticeHistory(): NoticeEntry[] {
  if (typeof window === 'undefined') return []
  try {
    return parseNoticeHistory(window.localStorage.getItem(NOTICE_STORAGE_KEY))
  } catch {
    return []
  }
}

export function persistNoticeHistory(history: NoticeEntry[]): void {
  if (typeof window === 'undefined') return
  try {
    window.localStorage.setItem(NOTICE_STORAGE_KEY, JSON.stringify(history))
  } catch {
    // Quota dépassé ou stockage désactivé : l'historique reste en mémoire.
  }
}
